import React from "react";
import { X } from "lucide-react";
import type { FilterValues } from "./ProductFilterPanel";

interface ActiveFiltersProps {
  filters: FilterValues;
  onRemoveFilter: (filterType: string, value?: string) => void;
  priceMin: number;
  priceMax: number;
}

const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  filters,
  onRemoveFilter,
  priceMin,
  priceMax,
}) => {
  const hasActiveFilters =
    filters.brands.length > 0 ||
    filters.rating !== null ||
    filters.inStock ||
    filters.onSale ||
    filters.freeShipping ||
    (filters.priceRange.min !== undefined &&
      filters.priceRange.min !== priceMin) ||
    (filters.priceRange.max !== undefined &&
      filters.priceRange.max !== priceMax);

  if (!hasActiveFilters) return null;

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-neutral-300">
          Active Filters:
        </span>
        <button
          onClick={() => onRemoveFilter("all")}
          className="text-xs text-primary hover:text-primary-dark font-semibold"
        >
          Clear All
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {/* Price Range Chip */}
        {((filters.priceRange.min !== undefined &&
          filters.priceRange.min !== priceMin) ||
          (filters.priceRange.max !== undefined &&
            filters.priceRange.max !== priceMax)) && (
          <button
            onClick={() => onRemoveFilter("price")}
            className="inline-flex items-center gap-1.5 bg-neutral-800 border border-neutral-700 text-neutral-300 px-3 py-1.5 rounded-full text-sm hover:bg-neutral-700 hover:text-white transition-colors"
          >
            <span>
              Price: ${filters.priceRange.min ?? priceMin} - $
              {filters.priceRange.max ?? priceMax}
            </span>
            <X className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Brand Chips */}
        {filters.brands.map((brand) => (
          <button
            key={brand}
            onClick={() => onRemoveFilter("brand", brand)}
            className="inline-flex items-center gap-1.5 bg-neutral-800 border border-neutral-700 text-neutral-300 px-3 py-1.5 rounded-full text-sm hover:bg-neutral-700 hover:text-white transition-colors"
          >
            <span>Brand: {brand}</span>
            <X className="w-3.5 h-3.5" />
          </button>
        ))}

        {/* Rating Chip */}
        {filters.rating !== null && (
          <button
            onClick={() => onRemoveFilter("rating")}
            className="inline-flex items-center gap-1.5 bg-neutral-800 border border-neutral-700 text-neutral-300 px-3 py-1.5 rounded-full text-sm hover:bg-neutral-700 hover:text-white transition-colors"
          >
            <span>{filters.rating}+ Stars</span>
            <X className="w-3.5 h-3.5" />
          </button>
        )}

        {/* In Stock Chip */}
        {filters.inStock && (
          <button
            onClick={() => onRemoveFilter("inStock")}
            className="inline-flex items-center gap-1.5 bg-neutral-800 border border-neutral-700 text-neutral-300 px-3 py-1.5 rounded-full text-sm hover:bg-neutral-700 hover:text-white transition-colors"
          >
            <span>In Stock</span>
            <X className="w-3.5 h-3.5" />
          </button>
        )}

        {/* On Sale Chip */}
        {filters.onSale && (
          <button
            onClick={() => onRemoveFilter("onSale")}
            className="inline-flex items-center gap-1.5 bg-neutral-800 border border-neutral-700 text-neutral-300 px-3 py-1.5 rounded-full text-sm hover:bg-neutral-700 hover:text-white transition-colors"
          >
            <span>On Sale</span>
            <X className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Free Shipping Chip */}
        {filters.freeShipping && (
          <button
            onClick={() => onRemoveFilter("freeShipping")}
            className="inline-flex items-center gap-1.5 bg-neutral-800 border border-neutral-700 text-neutral-300 px-3 py-1.5 rounded-full text-sm hover:bg-neutral-700 hover:text-white transition-colors"
          >
            <span>Free Shipping</span>
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ActiveFilters;
